import { useEffect } from 'react';
import { Box } from '@chakra-ui/react';
import ConfirmModal from '../../../common/ConfirmModal';
import { useUpgradeCta } from '../../../hooks/useUpgradeCta';
import { useTemplateLimitReached } from '../../../hooks/useTemplateLimitReached';
import { CustomLockIcon } from '../../../theme/icons/CustomLockIcon';

const INCREASE_LIMIT_COPY =
  "To publish another template, you'll need to increase the limit in your plan.";

type PublishLimitReachedModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSaveAsDraft?: () => void;
  saveAsDraftLabel?: string;
};

const PublishLimitReachedModal = ({
  isOpen,
  onClose,
  onSaveAsDraft,
  saveAsDraftLabel = 'Save draft',
}: PublishLimitReachedModalProps) => {
  const upgradeCta = useUpgradeCta();
  const { currentValue, limitValue, refresh } = useTemplateLimitReached();
  const hasCount = Number.isFinite(limitValue) && limitValue > 0;

  // We may only be here because the server rejected a publish with a 402, which
  // means our cached count is behind. Re-read it before showing a number.
  useEffect(() => {
    if (isOpen) refresh();
  }, [isOpen, refresh]);

  const dismiss = { label: 'Not now', onClick: onClose };

  return (
    <ConfirmModal
      isOpen={isOpen}
      onClose={onClose}
      icon={
        <CustomLockIcon
          boxSize="21px"
          fill="grey"
          data-testid="template-limit-lock"
        />
      }
      title="You've reached your template limit"
      description={
        hasCount ? (
          <>
            You have used{' '}
            <Box as="span" fontWeight="700">
              {currentValue}/{limitValue} templates
            </Box>
            . {INCREASE_LIMIT_COPY}
          </>
        ) : (
          INCREASE_LIMIT_COPY
        )
      }
      primaryButton={{
        label: upgradeCta.label,
        linkProps: upgradeCta.linkProps,
      }}
      secondaryButton={
        onSaveAsDraft
          ? {
              label: saveAsDraftLabel,
              variant: 'secondary',
              size: 'md',
              w: '100%',
              onClick: onSaveAsDraft,
            }
          : { ...dismiss, variant: 'outline', size: 'md', w: '100%' }
      }
      tertiaryButton={onSaveAsDraft ? dismiss : undefined}
    />
  );
};

export default PublishLimitReachedModal;
